/**
 * @file ClientSettingAdminAudience.js
 * @description Component to view and edit the Admin Audience client option.
 */

import { useEffect, useState } from 'react';
import {
    createClientOption,
    getClientOptions,
    updateClientOption,
} from '../../services/clientService';
import { publishAdminAudienceEnabledUpdate } from '../../hooks/useAdminAudienceEnabled';
import Toast from '../ui/toasts/Toast';
import { ClientOptionEnum, SecurityAttributes } from '../../utils/data/enums';
import { useAuthContext } from '../../store/Contexts';
import { getText } from '../../i18n';

function ClientSettingAdminAudience() {
    const [isOpen, setIsOpen] = useState(true);
    const [initialAdminAudienceSetting, setInitialAdminAudienceSetting] =
        useState({});
    const [selectedAdminAudienceEnabled, setSelectedAdminAudienceEnabled] =
        useState('false');
    const { hasAttribute } = useAuthContext();

    useEffect(() => {
        async function loadAdminAudienceOption() {
            try {
                const data = await getClientOptions(0, [
                    ClientOptionEnum.AdminAudienceEnabled,
                ]);
                const optionObject = data.find(
                    item =>
                        item.clientOptionId ===
                        ClientOptionEnum.AdminAudienceEnabled,
                );

                if (!optionObject) {
                    setInitialAdminAudienceSetting({
                        clientOptionId: ClientOptionEnum.AdminAudienceEnabled,
                        usingDefaultValue: true,
                        value: 'false',
                    });
                    setSelectedAdminAudienceEnabled('false');
                    return;
                }

                setInitialAdminAudienceSetting(optionObject);
                setSelectedAdminAudienceEnabled(optionObject.value);
            } catch (error) {
                console.error('Could not fetch Admin Audience setting:', error);
                Toast.error(
                    getText('msg_cc9c0e353072'),
                );
            }
        }

        loadAdminAudienceOption();
    }, []);

    function toggleAccordion() {
        setIsOpen(!isOpen);
    }

    function handleToggleAudienceEnabled() {
        setSelectedAdminAudienceEnabled(prev =>
            prev === 'true' ? 'false' : 'true',
        );
    }

    async function handleSave() {
        let noChanges = true;

        if (initialAdminAudienceSetting.usingDefaultValue) {
            noChanges = false;
            const option = {
                clientOptionId: ClientOptionEnum.AdminAudienceEnabled,
                value: selectedAdminAudienceEnabled,
            };
            const response = await createClientOption(option, 0);

            if (response.error) {
                Toast.error(
                    getText('msg_0505b11eb4c8'),
                );
                return;
            }

            setInitialAdminAudienceSetting(response.data);
        }

        if (
            !initialAdminAudienceSetting.usingDefaultValue &&
            initialAdminAudienceSetting.value !== selectedAdminAudienceEnabled
        ) {
            noChanges = false;
            const option = {
                clientOptionId: ClientOptionEnum.AdminAudienceEnabled,
                value: selectedAdminAudienceEnabled,
            };
            const response = await updateClientOption(option, 0);

            if (response.error) {
                Toast.error(
                    getText('msg_fbbc16ee16fe'),
                );
                return;
            }

            setInitialAdminAudienceSetting(response.data);
        }

        if (noChanges) {
            Toast.warning(getText('msg_13c3a7ac4e47'));
            return;
        }

        publishAdminAudienceEnabledUpdate(selectedAdminAudienceEnabled);

        Toast.success(getText('msg_a3d10ada7088'));
    }

    function handleCancel() {
        setSelectedAdminAudienceEnabled(initialAdminAudienceSetting.value);
    }

    return (
        <div
            style={{
                overflow: 'hidden',
                borderRadius: '12px',
                border: 'solid 1px rgba(47, 43, 67, 0.2)',
                marginTop: '10px',
                boxShadow:
                    'rgba(47, 43, 67, 0.1) 0px -1px 0px 0px inset, rgba(47, 43, 67, 0.1) 0px 1px 3px 0px',
            }}
        >
            <div
                style={{
                    overflowY: 'auto',
                    padding: '16px',
                    resize: 'none',
                    boxShadow:
                        'rgba(47, 43, 67, 0.1) 0px -1px 0px 0px inset, rgba(47, 43, 67, 0.1) 0px 1px 3px 0px;',
                }}
            >
                <div>
                    <div style={{ marginLeft: '12px' }}>
                        {getText('msg_69860c31e71c')}
                        <i
                            style={{
                                color: 'var(--iNGAGED-Neutral-Color3)',
                                display: 'flex',
                                float: 'inline-end',
                                cursor: 'pointer',
                            }}
                            className={`bi bi-chevron-${isOpen ? 'up' : 'down'}`}
                            onClick={toggleAccordion}
                        />
                    </div>
                </div>
                {isOpen && (
                    <div
                        style={{
                            marginLeft: '48px',
                            marginRight: '48px',
                            marginTop: '12px',
                            color: 'var(--iNGAGED-Neutral-Color2)',
                        }}
                    >
                        <div style={{ marginTop: '12px' }}>
                            <p>
                                {getText('msg_71b441a32da7')}
                            </p>
                        </div>

                        <div style={{ marginLeft: '24px' }}>
                            <div
                                style={{
                                    display: 'flex',
                                    gap: '5px',
                                    alignItems: 'anchor-center',
                                    marginTop: '12px',
                                }}
                            >
                                <div className="form-switch">
                                    <input
                                        onClick={handleToggleAudienceEnabled}
                                        className="form-check-input custom-switch-input"
                                        type="checkbox"
                                        checked={
                                            selectedAdminAudienceEnabled ===
                                            'true'
                                        }
                                    />
                                </div>
                                <div>{getText('msg_c5fb72f2eea0')}</div>
                            </div>
                        </div>
                        {hasAttribute(
                            SecurityAttributes.ClientSettingsFullAccess
                        ) && (
                                <div className="d-flex justify-content-evenly mt-5">
                                    <button
                                        type="button"
                                        onClick={handleCancel}
                                        className="ing-popup-blue-negative"
                                    >
                                        {getText('msg_77dfd2135f4d')}
                                    </button>
                                    <button
                                        type="button"
                                        className="ing-button-primary-none"
                                        onClick={handleSave}
                                    >
                                        {getText('msg_efc007a393f6')}
                                    </button>
                                </div>
                            )}
                    </div>
                )}
            </div>
        </div>
    );
}

export default ClientSettingAdminAudience;
